import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Modal, Button } from './ui';
import { createShareLink } from '../services/api';

interface ShareLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  messageIds: string[];
  onError?: (message: string) => void;
}

const ShareLinkModal: React.FC<ShareLinkModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  messageIds,
  onError,
}) => {
  const [title, setTitle] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleGenerateLink = async () => {
    // Clear previous errors
    setModalError(null);

    // Validate title length
    if (title && title.length > 200) {
      setModalError('Title must be 200 characters or less');
      return;
    }

    // Validate expiration date
    const expiresAtDate = expiresAt ? new Date(expiresAt) : undefined;
    if (expiresAtDate) {
      if (isNaN(expiresAtDate.getTime())) {
        setModalError('Invalid expiration date format');
        return;
      }
      if (expiresAtDate <= new Date()) {
        setModalError('Expiration date must be in the future');
        return;
      }
    }

    setIsGenerating(true);
    try {
      const result = await createShareLink(sessionId, messageIds, title || undefined, expiresAtDate);

      if (result.success && result.shareLink?.shareLinkId) {
        // Generate full URL
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/share/${result.shareLink.shareLinkId}`;
        setShareUrl(url);
      } else {
        throw new Error('Failed to create share link');
      }
    } catch (error: any) {
      console.error('Failed to generate share link:', error);
      setModalError(error.message || 'Failed to generate share link. Please try again.');
      // Also notify parent if callback provided
      if (onError) {
        onError(error.message || 'Failed to generate share link. Please try again.');
      }
      // Don't close modal - let user see the error and try again
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      if (onError) {
        onError('Failed to copy to clipboard');
      }
    }
  };

  const handleClose = () => {
    setTitle('');
    setExpiresAt('');
    setShareUrl(null);
    setCopied(false);
    setModalError(null);
    onClose();
  };

  const styles: { [key: string]: React.CSSProperties } = {
    formGroup: {
      marginBottom: '1rem',
    },
    label: {
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      color: 'var(--fg-2)',
      marginBottom: 6,
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid var(--border-2)',
      borderRadius: 'var(--radius-md)',
      fontSize: 14,
      color: 'var(--fg-1)',
      backgroundColor: '#FFFFFF',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
    },
    urlContainer: {
      backgroundColor: 'var(--bg-surface-3)',
      padding: '0.75rem',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-1)',
      marginBottom: '0.5rem',
    },
    url: {
      fontSize: 13,
      color: 'var(--accent)',
      wordBreak: 'break-all',
      fontFamily: 'var(--font-mono, monospace)',
    },
    helpText: {
      fontSize: 12.5,
      color: 'var(--fg-3)',
      marginTop: '0.25rem',
    },
    errorMessage: {
      backgroundColor: 'var(--danger-soft)',
      border: '1px solid var(--cg-terra-200)',
      borderRadius: 'var(--radius-md)',
      padding: '0.75rem',
      marginBottom: '1rem',
      fontSize: 13,
      fontWeight: 500,
      color: 'var(--danger)',
    },
  };

  // Ensure document.body exists before rendering portal
  if (typeof document === 'undefined' || !document.body) {
    console.error('[ShareLinkModal] document.body not available');
    return null;
  }

  const footer = !shareUrl ? (
    <>
      <Button variant="ghost" onClick={handleClose} disabled={isGenerating}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleGenerateLink} disabled={isGenerating}>
        {isGenerating ? 'Generating...' : 'Generate Link'}
      </Button>
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={handleClose}>
        Close
      </Button>
      <Button variant="subtle" onClick={handleCopyToClipboard}>
        {copied ? '✓ Copied!' : 'Copy to Clipboard'}
      </Button>
    </>
  );

  // Use React Portal to render modal at document.body level
  // This breaks out of parent component CSS inheritance
  const modalContent = (
    <Modal
      open
      onClose={handleClose}
      title="Share via Link"
      width={500}
      ariaLabel="Share via link"
      footer={footer}
    >
      {/* Error Message */}
      {modalError && (
        <div style={styles.errorMessage}>
          {modalError}
        </div>
      )}

      {!shareUrl ? (
        <>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="share-title">
              Title (optional)
            </label>
            <input
              id="share-title"
              type="text"
              style={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Q4 Revenue Analysis"
              disabled={isGenerating}
            />
            <div style={styles.helpText}>
              Add a descriptive title to help identify this conversation
            </div>
          </div>

          <div style={{ ...styles.formGroup, marginBottom: 0 }}>
            <label style={styles.label} htmlFor="share-expires">
              Expires at (optional)
            </label>
            <input
              id="share-expires"
              type="datetime-local"
              style={styles.input}
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              disabled={isGenerating}
            />
            <div style={styles.helpText}>
              Leave empty for no expiration
            </div>
          </div>
        </>
      ) : (
        <div style={{ ...styles.formGroup, marginBottom: 0 }}>
          <label style={styles.label}>Shareable URL</label>
          <div style={styles.urlContainer}>
            <div style={styles.url}>{shareUrl}</div>
          </div>
          <div style={styles.helpText}>
            Anyone at Culture Genesis with this link can view this conversation
          </div>
        </div>
      )}
    </Modal>
  );

  // Render modal using React Portal at document.body level
  return ReactDOM.createPortal(modalContent, document.body);
};

export default ShareLinkModal;
